/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Compose the chrome — top bar, rail, list, main, tutor thread and overlays.
 * @state-owner React Router entry path plus the navigation store
 * @allowed-imports React; React Router; app providers; auth module public API; shared components; shell parts
 * @forbidden in-product role switching; direct API client; module internals
 * @public-api app router
 * @side-effects navigation, CSS custom property updates and transient UI feedback
 * @decision The shell is mounted only while an active context exists. Clearing that context is the
 *   single way back to the role gate, which is why changing role costs no sign-in and why no
 *   module state can survive the change.
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ActiveRoleProvider } from '../app/providers/ActiveRoleProvider';
import { useWorkspaceSession } from '../app/providers/SessionProvider';
import { WorkspaceDataHydrator } from '../app/providers/WorkspaceDataHydrator';
import { TutorThread, useParsingSimulation } from '../modules/activities';
import { SignInScreen, useActiveContext, useSelectActiveContext } from '../modules/auth';
import { OverlayHost } from '../shared/components/OverlayHost';
import type { WorkspaceRole, WorkspaceSection } from '../shared/lib/model';
import { defaultSelection } from '../shared/lib/selectors';
import { useDatasetStore } from '../shared/model/dataset-store';
import { PANEL_CSS_VARIABLE, useWorkspaceUiStore } from '../shared/model/workspace-store';
import { RoleSelection } from './RoleSelection';
import { CommandPalette } from './shell/CommandPalette';
import { ListColumn } from './shell/ListColumn';
import { MainColumn } from './shell/MainColumn';
import { SearchOverlay } from './shell/SearchOverlay';
import { ShellKeyboard } from './shell/ShellKeyboard';
import { Toast } from './shell/Toast';
import { TopBar } from './shell/TopBar';
import { WorkspaceRail } from './shell/WorkspaceRail';

const PATH_SECTION: Readonly<Record<string, WorkspaceSection>> = {
  '/activities': 'act',
  '/classes': 'room',
  '/live': 'live',
  '/reports': 'rep',
};

export function WorkspaceShell() {
  const location = useLocation();
  const sessionState = useWorkspaceSession();
  const activeContextQuery = useActiveContext();
  const selectActiveContext = useSelectActiveContext();
  const role = activeContextQuery.data?.role ?? null;

  if (sessionState.isLoading) return <WorkspaceGate title="正在確認登入狀態…" />;

  if (sessionState.error) {
    return (
      <WorkspaceGate
        action={{ label: '重試', onClick: () => void sessionState.refresh() }}
        description={sessionState.error.message}
        title="無法確認登入狀態"
      />
    );
  }

  if (!sessionState.session) return <SignInScreen />;

  if (activeContextQuery.isLoading) return <WorkspaceGate title="正在載入你的工作區…" />;

  if (activeContextQuery.isError) {
    return (
      <WorkspaceGate
        action={{ label: '重試', onClick: () => void activeContextQuery.refetch() }}
        description="登入仍然有效，請重新載入由 Vispo API 管理的身分內容。"
        title="工作區暫時無法載入"
      />
    );
  }

  // No active context means no workspace, whether this is a first login or someone who just chose
  // 「切換身分」. One branch serves both, so leaving a workspace needs no route of its own — and
  // returning here unmounts the whole shell, which is what guarantees the next role starts with no
  // leftover panel, dialog or selection from the previous one.
  if (!role) {
    return (
      <RoleSelection
        busy={selectActiveContext.isPending}
        error={selectActiveContext.error?.message ?? null}
        onSelect={(nextRole) => selectActiveContext.mutate(nextRole)}
      />
    );
  }

  return (
    <ActiveRoleProvider contextId={activeContextQuery.data?.id ?? 'inactive'} role={role}>
      <OverlayHost>
        <WorkspaceDataHydrator
          activeContextId={activeContextQuery.data?.id ?? 'inactive'}
          role={role}
        />
        <WorkspaceChrome entryPath={location.pathname} role={role} />
      </OverlayHost>
    </ActiveRoleProvider>
  );
}

function WorkspaceChrome({ entryPath, role }: { entryPath: string; role: WorkspaceRole }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const seededRef = useRef(false);
  const panelWidths = useWorkspaceUiStore((state) => state.panelWidths);
  const go = useWorkspaceUiStore((state) => state.go);
  const classes = useDatasetStore((state) => state.classes);
  const runs = useDatasetStore((state) => state.runs);

  useParsingSimulation();

  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    const section = PATH_SECTION[entryPath] ?? 'room';
    go(section, defaultSelection(section, role, classes, runs));
  }, [classes, entryPath, go, role, runs]);

  useEffect(() => {
    for (const [key, variable] of Object.entries(PANEL_CSS_VARIABLE)) {
      const width = panelWidths[key as keyof typeof panelWidths];
      document.documentElement.style.setProperty(variable, `${width}px`);
    }
  }, [panelWidths]);

  const navigateSection = (section: WorkspaceSection) =>
    go(section, defaultSelection(section, role, classes, runs));

  return (
    <>
      <TopBar onOpenSearch={() => setSearchOpen(true)} />
      <div className="app">
        <WorkspaceRail onNavigate={navigateSection} />
        <div className="shellcard">
          <ListColumn onOpenSearch={() => setSearchOpen(true)} />
          <MainColumn />
          <TutorThread />
        </div>
      </div>
      <SearchOverlay onClose={() => setSearchOpen(false)} open={searchOpen} />
      <CommandPalette
        onClose={() => setPaletteOpen(false)}
        onNavigateSection={navigateSection}
        open={paletteOpen}
      />
      <ShellKeyboard
        onCloseSearch={() => setSearchOpen(false)}
        onNavigateSection={navigateSection}
        onOpenPalette={() => setPaletteOpen(true)}
      />
      <Toast />
    </>
  );
}

function WorkspaceGate({
  action,
  description,
  title,
}: {
  action?: { label: string; onClick(): void };
  description?: string;
  title: string;
}) {
  return (
    <main className="workspace-gate">
      <div className="jlogo">V</div>
      <strong>{title}</strong>
      {description ? <p>{description}</p> : null}
      {action ? (
        <button className="bt pri" type="button" onClick={action.onClick}>
          {action.label}
        </button>
      ) : null}
    </main>
  );
}
